import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import { steganographyAPI, EncodeResponse } from '../services/api';
import ImageComparison from './ImageComparison';
import MetricsPanel from './MetricsPanel';

interface EncodeTabProps {
  onImageProcessed?: () => void;
  onMessageEncoded?: () => void;
}


// Styled components for NEXUS design
const EncodeContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '30px',
}));

const SectionPanel = styled(Box)(({ theme }) => ({
  border: '1px solid #00F0FF',
  backgroundColor: 'rgba(11, 8, 19, 0.6)',
  padding: '25px',
  borderRadius: '5px',
  boxShadow: '0 0 15px rgba(0, 240, 255, 0.1)',
  [theme.breakpoints.down('md')]: {
    padding: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '15px',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 'bold',
  color: '#00F0FF',
  fontFamily: '"Press Start 2P", cursive',
  marginBottom: '20px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  textShadow: '0 0 10px rgba(0, 240, 255, 0.5)',
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
    marginBottom: '15px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
    marginBottom: '10px',
  },
}));

const DropzoneArea = styled(Box)<{ isdragactive: boolean }>(({ isdragactive, theme }) => ({
  border: isdragactive ? '2px dashed #00F0FF' : '2px dashed #6B6A78',
  borderRadius: '5px',
  padding: '40px',
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: isdragactive ? 'rgba(0, 240, 255, 0.1)' : 'transparent',
  minHeight: '200px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('md')]: {
    padding: '30px',
    minHeight: '150px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '20px',
    minHeight: '120px',
  },
  '&:hover': {
    borderColor: '#00F0FF',
    boxShadow: '0 0 20px rgba(0, 240, 255, 0.2)',
  },
}));

const DropzoneIcon = styled(Box)(({ theme }) => ({
  fontSize: '48px',
  color: '#6B6A78',
  marginBottom: '15px',
  [theme.breakpoints.down('md')]: {
    fontSize: '36px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '28px',
    marginBottom: '10px',
  },
}));

const DropzoneText = styled(Typography)(({ theme }) => ({
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '14px',
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
  },
}));

const DropzoneSubtext = styled(Typography)(({ theme }) => ({
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '11px',
  marginTop: '8px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '9px',
  },
}));

const NexusTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderColor: '#6B6A78',
    color: '#FFFFFF',
    fontFamily: '"JetBrains Mono", monospace',
    '& fieldset': {
      borderColor: '#6B6A78',
    },
    '&:hover fieldset': {
      borderColor: '#00F0FF',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00F0FF',
      boxShadow: '0 0 10px rgba(0, 240, 255, 0.3)',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#6B6A78',
    fontFamily: '"Press Start 2P", cursive',
    fontSize: '10px',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#00F0FF',
  },
  '& .MuiInputBase-input': {
    color: '#FFFFFF',
  },
}));

const NexusSelect = styled(Select)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderColor: '#6B6A78',
    color: '#FFFFFF',
    fontFamily: '"JetBrains Mono", monospace',
    '& fieldset': {
      borderColor: '#6B6A78',
    },
    '&:hover fieldset': {
      borderColor: '#00F0FF',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00F0FF',
    },
  },
}));

const NexusButton = styled(Button)<{ nexusVariant?: 'primary' | 'secondary' | 'danger' | string }>(({ nexusVariant = 'primary', theme }) => ({
  border: nexusVariant === 'primary' ? '2px solid #00F0FF' : 
           nexusVariant === 'secondary' ? '2px solid #FFD200' : 
           '2px solid #FF007A',
  color: nexusVariant === 'primary' ? '#00F0FF' : 
         nexusVariant === 'secondary' ? '#FFD200' : 
         '#FF007A',
  padding: '12px 25px',
  fontFamily: '"Press Start 2P", cursive',
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('md')]: {
    padding: '10px 20px',
    fontSize: '10px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px 15px',
    fontSize: '9px',
  },
  '&:hover': {
    borderColor: nexusVariant === 'primary' ? '#00F0FF' : 
             nexusVariant === 'secondary' ? '#FFD200' : 
             '#FF007A',
    color: nexusVariant === 'primary' ? '#00F0FF' : 
          nexusVariant === 'secondary' ? '#FFD200' : 
          '#FF007A',
    boxShadow: `0 0 15px ${nexusVariant === 'primary' ? 'rgba(0, 240, 255, 0.5)' : 
                           nexusVariant === 'secondary' ? 'rgba(255, 210, 0, 0.5)' : 
                           'rgba(255, 0, 122, 0.5)'}`,
    transform: 'translateY(-2px)',
  },
  '&:active': {
    backgroundColor: nexusVariant === 'primary' ? '#00F0FF' : 
                   nexusVariant === 'secondary' ? '#FFD200' : 
                   '#FF007A',
    color: '#0B0813',
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

const StatusMessage = styled(Box)<{ type: 'error' | 'success' }>(({ type, theme }) => ({
  padding: '15px',
  border: `1px solid ${type === 'error' ? '#FF007A' : '#39FF14'}`,
  backgroundColor: type === 'error' ? 'rgba(255, 0, 122, 0.1)' : 'rgba(57, 255, 20, 0.1)',
  borderRadius: '5px',
  color: type === 'error' ? '#FF007A' : '#39FF14',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '13px',
  marginBottom: '20px',
  boxShadow: type === 'error' ? '0 0 15px rgba(255, 0, 122, 0.2)' : '0 0 15px rgba(57, 255, 20, 0.2)',
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
    padding: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px',
    padding: '10px',
  },
}));

const ImagePreview = styled(Box)(({ theme }) => ({
  marginTop: '20px',
  padding: '15px',
  border: '1px solid #39FF14',
  backgroundColor: 'rgba(57, 255, 20, 0.05)',
  borderRadius: '5px',
  [theme.breakpoints.down('sm')]: {
    padding: '10px',
    marginTop: '15px',
  },
}));

const PreviewImage = styled('img')(({ theme }) => ({
  width: '100%',
  maxHeight: '200px',
  objectFit: 'contain',
  border: '1px solid #39FF14',
  [theme.breakpoints.down('sm')]: {
    maxHeight: '150px',
  },
}));

const FileInfo = styled(Typography)(({ theme }) => ({
  color: '#39FF14',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '11px',
  marginTop: '10px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
  },
}));

const HelperText = styled(Typography)(({ theme }) => ({
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '11px',
  marginTop: '5px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
  },
}));

const EncodeTab: React.FC<EncodeTabProps> = ({ onImageProcessed, onMessageEncoded }) => {
  const [originalImage, setOriginalImage] = useState<File | null>(null);
  const [originalImageUrl, setOriginalImageUrl] = useState<string>('');
  const [encodedImage, setEncodedImage] = useState<string>('');
  const [message, setMessage] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [bitDepth, setBitDepth] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [metrics, setMetrics] = useState<any>(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.png', '.bmp', '.jpg', '.jpeg']
    },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024, // 50MB
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setOriginalImage(file);
        const url = URL.createObjectURL(file);
        setOriginalImageUrl(url);
        setEncodedImage('');
        setMetrics(null);
        setError('');
        setSuccess('');
      }
    }
  });


  const handleEncode = async () => {
    // Image validation
    if (!originalImage) {
      setError('Please select an image');
      return;
    }
    
    // Validate image type
    const validImageTypes = ['image/png', 'image/bmp', 'image/jpeg', 'image/jpg'];
    if (!validImageTypes.includes(originalImage.type)) {
      setError('Invalid image type. Please use PNG, BMP, or JPEG.');
      return;
    }
    
    // Validate image size (max 50MB)
    const maxSize = 50 * 1024 * 1024;
    if (originalImage.size > maxSize) {
      setError('Image size exceeds 50MB limit. Please use a smaller image.');
      return;
    }
    
    // Message validation
    if (!message.trim()) {
      setError('Please enter a message to hide');
      return;
    }
    
    if (message.length > 100000) {
      setError('Message is too long. Maximum 100,000 characters allowed.');
      return;
    }
    
    if (message.length < 1) {
      setError('Message cannot be empty.');
      return;
    }
    
    // Password validation
    if (!password) {
      setError('Please enter a password');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    
    if (password.length > 128) {
      setError('Password is too long. Maximum 128 characters allowed.');
      return;
    }
    
    if (!/[A-Z]/.test(password)) {
      setError('Password must contain at least one uppercase letter.');
      return;
    }
    
    if (!/[a-z]/.test(password)) {
      setError('Password must contain at least one lowercase letter.');
      return;
    }
    
    if (!/[0-9]/.test(password)) {
      setError('Password must contain at least one number.');
      return;
    }
    
    // Bit depth validation
    if (![1, 2, 4].includes(bitDepth)) {
      setError('Invalid bit depth. Must be 1, 2, or 4.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response: EncodeResponse = await steganographyAPI.encode(
        originalImage,
        message,
        password,
        bitDepth
      );

      if (response.success && response.encoded_image) {
        setEncodedImage(`data:image/png;base64,${response.encoded_image}`);
        setMetrics(response.metrics);
        setSuccess('Message successfully encoded in image!');
        onImageProcessed?.();
        onMessageEncoded?.();
      } else {
        // Provide more specific error messages
        if (response.error?.includes('too large')) {
          setError('Image is too large for encoding. Please use a smaller image (under 64M pixels).');
        } else if (response.error?.includes('memory limit')) {
          setError('Server memory limit exceeded. Please try with a smaller image or reduce the message size.');
        } else {
          setError(response.error || 'Encoding failed');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Encoding failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (encodedImage) {
      const link = document.createElement('a');
      link.href = encodedImage;
      link.download = 'encoded_image.png';
      link.click();
    }
  };

  const handleClear = () => {
    setOriginalImage(null);
    setOriginalImageUrl('');
    setEncodedImage('');
    setMessage('');
    setPassword('');
    setMetrics(null);
    setError('');
    setSuccess('');
  };

  const getMessageCharCount = () => {
    return message.length;
  };

  const getEstimatedCapacity = () => {
    if (!originalImage) return 'N/A';
    
    // Create a temporary image to get dimensions
    const img = new Image();
    img.onload = function() {
      // Rough estimation: width * height * 3 channels * bit_depth / 8 bytes
      const estimatedSize = Math.floor((img.width * img.height * 3 * bitDepth) / 8);
      return `${estimatedSize} bytes`;
    };
    img.onerror = function() {
      return 'N/A';
    };
    img.src = originalImageUrl;
    
    return 'Loading...';
  };

  return (
    <EncodeContainer>
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Image Upload Section */}
        <Grid item xs={12} md={6}>
          <SectionPanel>
            <SectionTitle>01 / INPUT FRAGMENTS</SectionTitle>
            
            <DropzoneArea {...getRootProps()} isdragactive={isDragActive}>
              <input {...getInputProps()} />
              <DropzoneIcon>📁</DropzoneIcon>
              <DropzoneText>
                {isDragActive ? 'DROP IMAGE HERE' : 'DRAG & DROP IMAGE OR CLICK TO SELECT'}
              </DropzoneText>
              <DropzoneSubtext>
                PNG, BMP, JPEG (MAX 50MB)
              </DropzoneSubtext>
            </DropzoneArea>

            {originalImageUrl && (
              <ImagePreview>
                <PreviewImage
                  src={originalImageUrl}
                  alt="Original"
                />
                <FileInfo>
                  // FILE: {originalImage?.name} ({(originalImage?.size || 0) / 1024} KB)
                </FileInfo>
              </ImagePreview>
            )}
          </SectionPanel>
        </Grid>

        {/* Message and Controls Section */}
        <Grid item xs={12} md={6}>
          <SectionPanel>
            <SectionTitle>02 / ENCRYPTION PARAMETERS</SectionTitle>

            <NexusTextField
              fullWidth
              multiline
              rows={4}
              label="SECRET MESSAGE"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              margin="normal"
            />
            <HelperText>
              // CHARACTER COUNT: {getMessageCharCount()}
            </HelperText>

            <NexusTextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="ENCRYPTION KEY"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
            />

            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <NexusButton
                nexusVariant="secondary"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? 'HIDE KEY' : 'SHOW KEY'}
              </NexusButton>
            </Box>

            <FormControl fullWidth margin="normal">
              <InputLabel style={{ color: '#6B6A78', fontFamily: '"Press Start 2P", cursive', fontSize: '10px' }}>BIT DEPTH</InputLabel>
              <NexusSelect
                value={bitDepth}
                onChange={(e) => setBitDepth(Number(e.target.value))}
                label="BIT DEPTH"
              >
                <MenuItem value={1}>1-BIT (MOST SECURE)</MenuItem>
                <MenuItem value={2}>2-BIT (BALANCED)</MenuItem>
                <MenuItem value={4}>4-BIT (MAXIMUM CAPACITY)</MenuItem>
              </NexusSelect>
            </FormControl>

            <HelperText>
              // ESTIMATED CAPACITY: {getEstimatedCapacity()}
            </HelperText>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <NexusButton
                nexusVariant="primary"
                onClick={handleEncode}
                disabled={loading || !originalImage || !message || !password}
                fullWidth
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : '🔒 ENCODE MESSAGE'}
              </NexusButton>
              <NexusButton
                nexusVariant="danger"
                onClick={handleClear}
                fullWidth
              >
                CLEAR
              </NexusButton>
            </Box>
          </SectionPanel>
        </Grid>

        {/* Results Section */}
        {error && (
          <Grid item xs={12}>
            <StatusMessage type="error">
              // ERROR: {error}
            </StatusMessage>
          </Grid>
        )}

        {success && (
          <Grid item xs={12}>
            <StatusMessage type="success">
              // SUCCESS: {success}
            </StatusMessage>
          </Grid>
        )}

        {encodedImage && (
          <>
            <Grid item xs={12} md={6}>
              <SectionPanel>
                <SectionTitle>03 / OUTPUT WORLD</SectionTitle>
                <ImagePreview>
                  <PreviewImage
                    src={encodedImage}
                    alt="Encoded"
                  />
                </ImagePreview>
                <NexusButton
                  nexusVariant="secondary"
                  onClick={handleDownload}
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  📥 DOWNLOAD ENCODED IMAGE
                </NexusButton>
              </SectionPanel>
            </Grid>

            <Grid item xs={12} md={6}>
              <MetricsPanel metrics={metrics} />
            </Grid>

            <Grid item xs={12}>
              <ImageComparison
                originalImage={originalImageUrl}
                encodedImage={encodedImage}
              />
            </Grid>
          </>
        )}
      </Grid>
    </EncodeContainer>
  );
};

export default EncodeTab;
